/**
 * Scene-driven Kitchen Showroom layout.
 *
 * Design model:
 *   - LAYOUT     : galley / compact / lshape  → 3 real base scenes per DOOR STYLE
 *   - DOOR_STYLE : 5 real Cabinet Depot door styles (Essex / York / Sturbridge /
 *                  Rockingham / Windsor) — visible in the door swatch thumbnails
 *   - COLOR      : 8 Cabinet Depot base colors — applied at render time via
 *                  Nano Banana image editing on top of the door style base scene
 *   - APPLIANCES : real Cabinet Depot inventory (fridges/ranges/dishwashers +
 *                  Element Cellara wine cooler)
 *
 * Base scenes: /api/static/scenes/kitchen_<style>_<layout>.png (15 total)
 *              These are all in the door style's default color; the color
 *              picker triggers an on-demand recolor via Nano Banana.
 */

export const CABINET_DEPOT_URL   = "https://cabinetdepot.com/products";
export const APPLIANCE_DEPOT_URL = "https://cabinetdepot.com/appliances";
export const REFRIGERATOR_URL    = "https://cabinetdepot.com/refrigerators";
export const RANGE_URL           = "https://cabinetdepot.com/ranges";
export const DISHWASHER_URL      = "https://cabinetdepot.com/dishwashers";

/* -------------------- Door Styles (real Cabinet Depot catalog) -------------------- */

export const DOOR_STYLES = [
  { id: "essex",      label: "Essex",      collection: "Legacy • Bright Shaker",         href: "https://cabinetdepot.com/legacy-series-doors" },
  { id: "york",       label: "York",       collection: "Legacy • Raised Panel",          href: "https://cabinetdepot.com/legacy-series-doors" },
  { id: "sturbridge", label: "Sturbridge", collection: "Signature • Transitional",        href: CABINET_DEPOT_URL },
  { id: "rockingham", label: "Rockingham", collection: "Signature • Clean Shaker",        href: CABINET_DEPOT_URL },
  { id: "windsor",    label: "Windsor",    collection: "Slim • Slim Shaker / Slab",       href: CABINET_DEPOT_URL },
];

export const DOOR_SWATCH = (styleId) => `/api/static/doors/${styleId}.png`;

/* -------------------- Cabinet Colors (Cabinet Depot's real Sherwin-Williams palette) --------------------
   Cabinet Depot offers "virtually any Sherwin-Williams color" but SHOWCASES 6
   core finishes on their door product pages (Essex Bright, York Sage, Sturbridge
   Sage/Caramel, Rockingham Navy/Caramel, Windsor Dove/Oak).  These are those 6
   colors, with hex values sampled from the actual Cabinet Depot door thumbnails
   plus their Sherwin-Williams color reference name.                             */

export const CABINET_COLORS = [
  { id: "white",   label: "Bright White",  sw: "SW 7008 Alabaster", swatch: "#F5F1E8",
    prompt: "Sherwin-Williams Alabaster (SW 7008) — a warm bright white painted finish with a subtle cream undertone, clean and crisp" },
  { id: "dove",    label: "Dove Gray",     sw: "SW 6385 Dover White",  swatch: "#DED9CD",
    prompt: "Cabinet Depot Windsor Dove — a soft warm dove gray painted finish with a hint of beige, matches Sherwin-Williams Dover White / Modest White" },
  { id: "sage",    label: "Sage Green",    sw: "SW 6204 Sea Salt",  swatch: "#8A9986",
    prompt: "Cabinet Depot Sage — a soft muted sage green painted finish, matches Sherwin-Williams Rosemary (SW 6187) or Sea Salt (SW 6204)" },
  { id: "navy",    label: "Navy Blue",     sw: "SW 6244 Naval",     swatch: "#253046",
    prompt: "Cabinet Depot Rockingham Navy — a deep rich navy blue painted finish, matches Sherwin-Williams Naval (SW 6244) or In The Navy (SW 9178)" },
  { id: "caramel", label: "Caramel",       sw: "Cabinet Depot Caramel Stain", swatch: "#C89663",
    prompt: "Cabinet Depot Caramel — a warm caramel honey-stained wood finish showing fine wood grain, medium tone" },
  { id: "oak",     label: "Natural Oak",   sw: "Cabinet Depot Natural Oak",   swatch: "#D2A56E",
    prompt: "Cabinet Depot Windsor Oak — natural oak wood grain finish, unstained honey tone with prominent vertical wood grain and visible knots" },
];

// The color a door style ships in by default (used when no color override is picked).
export const DEFAULT_COLOR_FOR_STYLE = {
  essex:      "white",
  york:       "sage",
  sturbridge: "sage",
  rockingham: "navy",
  windsor:    "dove",
};

/* -------------------- Wall tint options -------------------- */

export const WALL_TINTS = [
  { id: "off_white", label: "Off White",  swatch: "#FFFDF5", rgba: "rgba(255, 253, 245, 0)"   },
  { id: "greige",    label: "Greige",     swatch: "#C8BFB0", rgba: "rgba(200, 191, 176, 0.14)" },
  { id: "sage",      label: "Sage",       swatch: "#B5C4A5", rgba: "rgba(181, 196, 165, 0.16)" },
  { id: "navy",      label: "Navy",       swatch: "#2C3E5D", rgba: "rgba(44,  62,  93,  0.18)" },
  { id: "terracotta",label: "Terracotta", swatch: "#C46C4C", rgba: "rgba(196, 108, 76,  0.14)" },
];

/* -------------------- Templates -------------------- */
/* Each template describes the SLOT POSITIONS on its 3 door-style base scenes.
   The scene file for template T + style S is: kitchen_<S>_<T>.png.
   Appliance MODEL lists are no longer stored here — they're fetched live from
   Cabinet Depot's Base44 API via GET /api/appliances and rendered by the
   Showroom wizard (see Showroom.jsx → useLiveAppliances).                     */

export const TEMPLATES = {
  galley: {
    id: "galley",
    label: "Galley",
    caption: "18′ straight-run kitchen — pantry, sink, range wall, fridge alcove",
    sceneRatio: 1408 / 768,
    slots: [
      { id: "range",      label: "Range",        left: 43, bottom:  6, width: 14, height: 36 },
      { id: "dishwasher", label: "Dishwasher",   left: 57, bottom:  6, width: 15, height: 36 },
      { id: "wine",       label: "Wine Cooler",  left: 85, bottom:  6, width:  7, height: 34 },
      { id: "fridge",     label: "Refrigerator", left: 92, bottom:  6, width:  7, height: 88 },
    ],
  },
  compact: {
    id: "compact",
    label: "Compact",
    caption: "10′ apartment kitchen — tight one-wall run, no wine slot",
    sceneRatio: 1408 / 768,
    slots: [
      { id: "range",      label: "Range",        left: 36, bottom:  6, width: 14, height: 36 },
      { id: "dishwasher", label: "Dishwasher",   left: 51, bottom:  6, width: 13, height: 36 },
      { id: "fridge",     label: "Refrigerator", left: 84, bottom:  6, width: 15, height: 88 },
    ],
  },
  lshape: {
    id: "lshape",
    label: "L-Shape",
    caption: "Corner kitchen — two perpendicular walls meeting at a 90° corner",
    sceneRatio: 1408 / 768,
    slots: [
      { id: "range",      label: "Range",        left: 42, bottom:  6, width: 12, height: 36 },
      { id: "dishwasher", label: "Dishwasher",   left: 58, bottom:  6, width: 12, height: 36 },
      { id: "fridge",     label: "Refrigerator", left: 84, bottom:  6, width: 14, height: 88 },
    ],
  },
};

export const TEMPLATE_LIST = [TEMPLATES.galley, TEMPLATES.compact, TEMPLATES.lshape];

/* Helper: build the base scene URL for a given door style + layout. */
export const SCENE_FILE = (styleId, layoutId) => `kitchen_${styleId}_${layoutId}.png`;
